import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { UserCheck, UserX, Loader2, PauseCircle, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";

const Avatar = ({ u }) => (
  <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-zinc-800 text-xs font-700 text-zinc-300">
    {u.picture ? <img src={u.picture} alt="" className="h-full w-full object-cover" /> : (u.name || u.email || "?").slice(0, 1).toUpperCase()}
  </div>
);

const timeAgo = (iso) => {
  if (!iso) return "Never active";
  const then = new Date(iso).getTime();
  if (isNaN(then)) return "Never active";
  const s = Math.floor((Date.now() - then) / 1000);
  if (s < 60) return "Active just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `Active ${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `Active ${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `Active ${d}d ago`;
  return `Active on ${new Date(iso).toLocaleDateString()}`;
};

const Row = ({ u, children }) => (
  <div className="flex items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2.5" data-testid={`user-row-${u.id}`}>
    <Avatar u={u} />
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-600 text-zinc-100" data-testid={`user-name-${u.id}`}>{u.name || "(no name)"}{u.role === "admin" ? " · Owner" : ""}</p>
      <p className="truncate text-xs text-zinc-400" data-testid={`user-email-${u.id}`}>{u.email}</p>
      {u.role !== "admin" && (
        <p className="truncate text-[11px] text-zinc-500" data-testid={`user-active-${u.id}`}>{timeAgo(u.last_active)}</p>
      )}
    </div>
    {children}
  </div>
);

export const UserAccess = ({ onChanged }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.adminListUsers().then((d) => { setData(d); onChanged?.(); }).catch(() => setData({ pending: [], approved: [], denied: [] }));
  }, [onChanged]);
  useEffect(() => { load(); }, [load]);

  const approve = async (u) => {
    setBusy(u.id);
    try { await api.adminApproveUser(u.id); toast.success(`Approved ${u.name || u.email}`); load(); }
    catch { toast.error("Could not approve"); } finally { setBusy(null); }
  };
  const deny = async (u) => {
    setBusy(u.id);
    try { await api.adminDenyUser(u.id); toast.success(`Denied ${u.name || u.email}`); load(); }
    catch { toast.error("Could not deny"); } finally { setBusy(null); }
  };
  const pause = async (u) => {
    if (!window.confirm(`Pause access for ${u.name || u.email}? They'll be signed out and can't use the app until you restore them. Their data is kept.`)) return;
    setBusy(u.id);
    try { await api.adminDenyUser(u.id); toast.success(`Paused ${u.name || u.email}`); load(); }
    catch { toast.error("Could not pause access"); } finally { setBusy(null); }
  };
  const restore = async (u) => {
    setBusy(u.id);
    try { await api.adminApproveUser(u.id); toast.success(`Restored ${u.name || u.email}`); load(); }
    catch { toast.error("Could not restore"); } finally { setBusy(null); }
  };

  const pending = data?.pending || [];
  const approved = data?.approved || [];
  const denied = data?.denied || [];

  return (
    <div className="space-y-5" data-testid="user-access-panel">
      <div>
        <p className="mb-2 text-xs font-700 uppercase tracking-wider text-[#E0575B]">
          Pending requests{pending.length ? ` (${pending.length})` : ""}
        </p>
        {data === null ? (
          <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>
        ) : pending.length === 0 ? (
          <p className="rounded-lg border border-dashed border-zinc-800 px-3 py-4 text-center text-sm text-zinc-500" data-testid="no-pending">No one is waiting for approval.</p>
        ) : (
          <div className="space-y-2">
            {pending.map((u) => (
              <Row key={u.id} u={u}>
                <div className="flex shrink-0 gap-2">
                  <button onClick={() => approve(u)} disabled={busy === u.id} className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1.5 text-xs font-600 text-white transition-colors hover:bg-emerald-500 disabled:opacity-50" data-testid={`approve-btn-${u.id}`}>
                    {busy === u.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserCheck className="h-3.5 w-3.5" />} Approve
                  </button>
                  <button onClick={() => deny(u)} disabled={busy === u.id} className="inline-flex items-center gap-1 rounded-md border border-zinc-700 px-2.5 py-1.5 text-xs font-600 text-zinc-300 transition-colors hover:text-red-400 disabled:opacity-50" data-testid={`deny-btn-${u.id}`}>
                    <UserX className="h-3.5 w-3.5" /> Deny
                  </button>
                </div>
              </Row>
            ))}
          </div>
        )}
      </div>

      <div>
        <p className="mb-2 text-xs font-700 uppercase tracking-wider text-zinc-400">Approved members ({approved.length})</p>
        <div className="space-y-2">
          {approved.map((u) => (
            <Row key={u.id} u={u}>
              {u.role === "admin"
                ? <span className="shrink-0 rounded-full bg-[#9B2226]/15 px-2 py-0.5 text-[10px] font-600 text-[#E0575B]">You</span>
                : (
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-600 text-emerald-400">Approved</span>
                    <button onClick={() => pause(u)} disabled={busy === u.id} className="inline-flex items-center gap-1 rounded-md border border-zinc-700 px-2.5 py-1.5 text-xs font-600 text-zinc-300 transition-colors hover:text-amber-400 disabled:opacity-50" data-testid={`pause-btn-${u.id}`}>
                      {busy === u.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PauseCircle className="h-3.5 w-3.5" />} Pause access
                    </button>
                  </div>
                )}
            </Row>
          ))}
        </div>
      </div>

      {denied.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-700 uppercase tracking-wider text-zinc-400">Paused / removed ({denied.length})</p>
          <div className="space-y-2">
            {denied.map((u) => (
              <Row key={u.id} u={u}>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-600 text-amber-400">Paused</span>
                  <button onClick={() => restore(u)} disabled={busy === u.id} className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1.5 text-xs font-600 text-white transition-colors hover:bg-emerald-500 disabled:opacity-50" data-testid={`restore-btn-${u.id}`}>
                    {busy === u.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Restore
                  </button>
                </div>
              </Row>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
